// src/data/loadSentences.js
import { TENSES } from "./tenseSchedule";
import { cachedByLang, fetchLangCsvLines } from "./loadLangCsv";

// Spanische Spaltennamen in es_sentences_cloze.csv → Schlüssel aus tenseSchedule.js
const CSV_TENSE_NAMES = {
  presente: "present",
  perfecto: "perfect",
  indefinido: "preterite",
  imperfecto: "imperfect",
  futuro: "future",
  condicional: "conditional",
  subj_presente: "subjunctive_present",
  subj_imperfecto: "subjunctive_imperfect",
  imperativo: "imperative",
};

function parseIntSafe(value, fallback = 0) {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
}

/**
 * Lädt die Sätze (Lückentexte + Freie Übersetzung) einer Lernsprache aus
 * /data/<lang>_sentences_cloze.csv. Fehlt die Datei, kommt eine leere Liste zurück.
 * Struktur der CSV (mindestens):
 * id;lang;src;target;morph_axis;clause_axis;length;[weitere Zeitform-Spalten...]
 * (src = Muttersprache, target = Lernsprache)
 *
 * Alle Spalten NACH "length" werden als numerische Zeitform-Tokens
 * in einem Array tense_tokens gespeichert, z.B.:
 *   tense_tokens: [1,0,0,0,0,0,0,0]
 *
 * Zusätzlich werden die benannten Zeitform-Spalten als einzelne Properties mit den
 * sprachneutralen Schlüsseln abgelegt (CSV tense_presente → tense_present, tense_indefinido
 * → tense_preterite …), fehlende Spalten = 0.
 */
export const loadSentences = cachedByLang(async (lang) => {
  const file = `${lang}_sentences_cloze.csv`;
  const lines = await fetchLangCsvLines(lang, "sentences_cloze");
  if (!lines) return [];

  const headerLine = lines[0];
  const headers = headerLine.split(";").map((h) => h.trim());

  const idxId = headers.indexOf("id");
  const idxLang = headers.indexOf("lang");
  const idxSrc = headers.indexOf("src");
  const idxTarget = headers.indexOf("target");
  const idxMorph = headers.indexOf("morph_axis");
  const idxClause = headers.indexOf("clause_axis");
  const idxLength = headers.indexOf("length");

  if (
    idxId === -1 ||
    idxLang === -1 ||
    idxSrc === -1 ||
    idxTarget === -1 ||
    idxMorph === -1 ||
    idxClause === -1 ||
    idxLength === -1
  ) {
    throw new Error(
      `Ungültige CSV-Header in ${file} – erwartet mindestens: id;lang;src;target;morph_axis;clause_axis;length`
    );
  }

  // Zeitform-Spalten → sprachneutrale Schlüssel aus tenseSchedule.js (tense_presente → tense_present);
  // Spalten, die schon neutral heißen (tense_perfect), werden direkt übernommen
  const tenseColumns = headers
    .map((h, idx) => ({ idx, tense: CSV_TENSE_NAMES[h.replace(/^tense_/, "")] || h.replace(/^tense_/, "") }))
    .filter(({ idx, tense }) => headers[idx].startsWith("tense_") && TENSES.includes(tense));

  const sentences = [];

  for (let i = 1; i < lines.length; i++) {
    const row = lines[i];
    if (!row) continue;

    const colsRaw = row.split(";");

    // Falls irgendwo ein ; im Text landet, rudimentär zusammenführen:
    let cols;
    if (colsRaw.length > headers.length) {
      const fixed = [];
      for (let h = 0; h < headers.length; h++) {
        if (h === headers.length - 1) {
          fixed[h] = colsRaw.slice(h).join(";");
        } else {
          fixed[h] = colsRaw[h];
        }
      }
      cols = fixed;
    } else {
      cols = colsRaw;
    }

    // Zeitform-Tokens: alle Spalten nach "length"
    const tenseTokens = [];
    for (let h = idxLength + 1; h < headers.length; h++) {
      tenseTokens.push(parseIntSafe(cols[h]));
    }

    const sentence = {
      id: (cols[idxId] || "").trim(),
      lang: (cols[idxLang] || "").trim(),
      src: (cols[idxSrc] || "").trim(),
      target: (cols[idxTarget] || "").trim(),
      morph_axis: parseIntSafe(cols[idxMorph]),
      clause_axis: parseIntSafe(cols[idxClause]),
      length: parseIntSafe(cols[idxLength]),
      // z.B. [Pres, Perfekt, Prät, Futur, Kond., Subj.Präs., Subj.Verg., Imperativ]
      tense_tokens: tenseTokens,

      // Benannte Zeitform-Flags (tense_present, tense_perfect …) – werden von sentenceMatchesTenseRules benutzt
      ...Object.fromEntries(TENSES.map((t) => [`tense_${t}`, 0])),
      ...Object.fromEntries(tenseColumns.map(({ idx, tense }) => [`tense_${tense}`, parseIntSafe(cols[idx])])),
    };

    sentences.push(sentence);
  }

  return sentences;
});
